/* eslint-disable react/jsx-no-literals */
// components/budget-panel/CurrencySelector.tsx
import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import { ReactComponent as ArrowDownIcon } from '@/assets/icons/arrow-down.svg';

interface Props {
  onChange: (currency: string) => void;
  value: string;
  exchangeRates?: Record<string, number>;
}

const CURRENCIES = [
  { code: 'GBP', symbol: '£', name: 'British Pound' },
  { code: 'USD', symbol: '$', name: 'US Dollar' },
  { code: 'EUR', symbol: '€', name: 'Euro' },
  { code: 'AUD', symbol: 'A$', name: 'Australian Dollar' },
  { code: 'CAD', symbol: 'C$', name: 'Canadian Dollar' },
  { code: 'AED', symbol: 'د.إ', name: 'UAE Dirham' },
  { code: 'JPY', symbol: '¥', name: 'Japanese Yen' },
  { code: 'CNY', symbol: '¥', name: 'Chinese Yuan' },
  { code: 'DKK', symbol: 'kr', name: 'Danish Krone' },
  { code: 'HKD', symbol: 'HK$', name: 'Hong Kong Dollar' },
  { code: 'INR', symbol: '₹', name: 'Indian Rupee' },
  { code: 'NOK', symbol: 'kr', name: 'Norwegian Krone' },
  { code: 'QAR', symbol: 'ر.ق', name: 'Qatari Riyal' },
  { code: 'SAR', symbol: 'ر.س', name: 'Saudi Riyal' },
  { code: 'SGD', symbol: 'S$', name: 'Singapore Dollar' },
  { code: 'SEK', symbol: 'kr', name: 'Swedish Krona' },
  { code: 'ZAR', symbol: 'R', name: 'South African Rand' },
  { code: 'PKR', symbol: '₨', name: 'Pakistani Rupee' },
  { code: 'NZD', symbol: 'NZ$', name: 'New Zealand Dollar' },
];

export const CurrencySelector: React.FC<Props> = ({ value, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const selectedCurrency = CURRENCIES.find(c => c.code === value) || CURRENCIES[0];

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  return (
    <Container>
      <Dropdown ref={dropdownRef}>
        <DropdownButton onClick={() => setIsOpen(!isOpen)}>
          <CurrencyCode>{selectedCurrency.code}</CurrencyCode>
          <CurrencyFlag>({selectedCurrency.symbol})</CurrencyFlag>
          <ArrowIcon $isOpen={isOpen} />
        </DropdownButton>

        {isOpen && (
          <DropdownMenu>
            {CURRENCIES.map(currency => (
              <DropdownItem
                key={currency.code}
                onClick={() => {
                  onChange(currency.code);
                  setIsOpen(false);
                }}
                $isSelected={currency.code === value}
              >
                <CurrencyInfo>
                  <CurrencyCode>{currency.code}</CurrencyCode>
                </CurrencyInfo>
                <CurrencyFlag>({currency.symbol})</CurrencyFlag>
              </DropdownItem>
            ))}
          </DropdownMenu>
        )}
      </Dropdown>
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const Dropdown = styled.div`
  position: relative;
`;

const DropdownButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.5rem 0.75rem;
  background: white;
  border: 1px solid ${({ theme }) => theme.palette.divider};
  border-radius: 0.5rem;
  cursor: pointer;
  transition: all 0.2s;
`;

const DropdownMenu = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  margin-top: 0.25rem;
  background: white;
  border: 1px solid ${({ theme }) => theme.palette.divider};
  border-radius: 0.5rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  z-index: 10;
  min-width: 50px;
  max-height: 300px;
  overflow-y: auto;
`;

const DropdownItem = styled.div<{ $isSelected: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem;
  cursor: pointer;
  background: ${({ $isSelected }) => ($isSelected ? '#f5f5f5' : 'transparent')};
  transition: background 0.2s;

  &:hover {
    background: #f0f0f0;
  }

  &:first-child {
    border-radius: 0.5rem 0.5rem 0 0;
  }

  &:last-child {
    border-radius: 0 0 0.5rem 0.5rem;
  }
`;

const CurrencyFlag = styled.span`
  font-size: 1rem;
  width: 1.5rem;
  text-align: center;
`;

const ArrowIcon = styled(ArrowDownIcon)<{ $isOpen: boolean }>`
  width: 0.75rem;
  height: 0.75rem;
  transition: transform 0.2s;
  transform: ${({ $isOpen }) => ($isOpen ? 'rotate(180deg)' : 'rotate(0deg)')};
`;

const CurrencyCode = styled.span`
  font-size: 1rem;
`;

const CurrencyInfo = styled.div`
  display: flex;
  flex-direction: column;
`;
